<template>
  <div class="vcl-logon-auth">
    <vp-icon icon="close" class="vcl-logon__close-button" @click="close" />
    <h3 class="vcl-logon__title">{{ $t('authPin') }}</h3>
    <p class="vcl-logon__description">{{ $t('checkEmailPin') }}</p>
    <vp-input
      v-model="token"
      autofocus
      width="100%"
      height="50px"
      :placeholder="$t('enterPin')"
      :type="inputType"
      maxlength="5"
      required
      :styles="inputStyles"
    >
      <template v-if="error && !token" #error>{{ error }}</template>
    </vp-input>
    <vp-button
      class="vcl-logon__button primary large"
      :disabled="!token"
      :styles="buttonStyles"
      @click="login"
    >
      {{ $t('signIn') }}
    </vp-button>
  </div>
</template>

<script>
// General Components
import VpIcon from '../../components/vp-icon/VpIcon.vue';
import VpInput from '../../components/vp-input/VpInput.vue';
import VpButton from '../../components/vp-button/VpButton.vue';

export default {
  name: 'VpAuth',
  components: {
    VpIcon,
    VpInput,
    VpButton,
  },
  inject: ['$busy', '$auth', '$screen'],
  props: {
    styles: {
      type: Object,
      default() {
        return {};
      },
    }
  },
  emits: ['auth'],
  data() {
    return {
      token: null,
      message: '',
    };
  },
  computed: {
    error() {
      return this.message;
    },
    inputStyles() {
      return this.styles.input || {};
    },
    buttonStyles() {
      return this.styles.button || {};
    },
    isMobile() {
      return this.$screen.width < 450;
    },
    inputType() {
      return this.isMobile ? 'number' : 'tel';
    }
  },
  created() {
    window.addEventListener('keyup', this.keyListener);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.keyListener);
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.$busy.on(true);
      const { logon, sso } = this.$deps;
      const { pin = false, uid, token = null, totp = false, passwordReset = false, samlToken = false } = this.$route.query;

      if (JSON.parse(pin)) {
        this.$busy.on(false);
        return null;
      }

      if (uid && token) {
        if (JSON.parse(totp)) {
          this.$busy.on(false);
          return this.$router.push({ path: '/totp', query: { uid, token, passwordReset } });
        }

        return logon.login({ uid, token, totp, usingPassword: false, usingSaml: false })
          .then((data) => {
            const { loggedIn = false, reason = null } = data;
            if (!loggedIn) {
              return this.errorHandler(reason);
            }

            this.$auth.set(true);
            if (JSON.parse(passwordReset)) {
              return this.$router.push({ path: '/my-account/security', query: {} });
            }

            return logon.loginRedirect();
          })
          .catch(() => {
            return this.$router.push({ path: '/logon/error', query: {} });
          })
          .finally(() => {
            this.$busy.on(false);
          });
      }

      if (uid && samlToken) {
        return logon.login({ uid, token: samlToken, totp: false, usingPassword: false, usingSaml: true })
          .then((data) => {
            const { loggedIn = false, reason = null } = data;
            if (!loggedIn) {
              return this.errorHandler(reason);
            }

            this.$auth.set(true);
            return logon.loginRedirect();
          })
          .catch(() => {
            return this.$router.push({ path: '/logon/error', query: {} });
          })
          .finally(() => {
            this.$busy.on(false);
          });
      }

      this.$busy.on(false);
      return null;
    },
    close() {
      return this.$router.push('/logon');
    },
    login() {
      this.$busy.on(true);
      const { logon } = this.$deps;

      const { uid, totp = false } = this.$route.query;

      if (JSON.parse(totp)) {
        this.$busy.on(false);
        // if totp, we pass uid and token (entered pin)
        return this.$router.push({ path: '/totp', query: { uid, token: this.token } });
      }

      return logon.login({ uid, token: this.token })
        .then((data) => {
          const { loggedIn = false, reason } = data;

          if (!loggedIn) {
            this.token = '';
            return this.errorHandler(reason);
          }

          this.$auth.set(true);
          return logon.loginRedirect();
        })
        .catch(() => {
          return this.$router.push({ path: '/logon/error', query: {} });
        })
        .finally(() => {
          this.$busy.on(false);
        });
    },
    keyListener(event) {
      if (event.key === 'Enter' && this.token) {
        return this.login();
      }

      if (event.key === 'Escape') {
        return this.close();
      }

      return null;
    },
    errorHandler(reason) {
      switch (reason) {
        case 'INACTIVE_LINK':
          return this.$router.push({ path: '/logon/error', query: { message: 'We’re sorry, your attempt to log into Virtual Peaker was unsuccessful. The link you clicked is no longer active. Please re-enter your email address and try again.' } });
        case 'LOCKED_OUT':
          return this.$router.push({ path: '/logon/error', query: { message: 'You have made too many failed login attempts. You have been locked out for 15 minutes.' } });
        case 'INCORRECT_PIN':
          this.message = 'Your PIN was incorrect';
          return null;
        default:
          this.message = 'Your login attempt failed.';
          return null;
      }
    },
  }
};
</script>

<style lang="scss" scoped>
  @import "../../styles/mixins.scss";

  .vcl-logon__close-button {
    cursor: pointer;
  }

  .vcl-logon-auth {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0;
    padding: 0px 30px 0px;
     @include Tablet {
      padding: 55px 47px 36px;
      .vcl-logon__description {
        margin-bottom: 37px;
      }
    }
  }

  h3 {
    margin-top:0px;
  }

  div.vcl-input {
    margin-bottom: 200px !important;
  }
</style>
